/**
 * Compute weak areas from Extended questionnaire data.
 * Replaces ComplianceAssessment.categoryBreakdown as source.
 */

export type WeakArea = { code: string; name: string; percent: number; standard?: string };

export type ExtendedBlockData = {
  blockStats?: Record<string, { yes: number; no: number; partial: number; n_a: number }>;
  overallStats?: { yes: number; no: number; partial: number; n_a: number; total: number };
};

function blockScoreFromStats(stats: { yes: number; no: number; partial: number; n_a: number }): number {
  const total = stats.yes + stats.no + stats.partial + stats.n_a;
  const applicable = total - (stats.n_a || 0);
  if (applicable <= 0) return 0;
  const score = stats.yes * 1 + stats.partial * 0.5;
  return Math.round((score / applicable) * 100);
}

/**
 * Get top 5 lowest categories (weak areas) from Extended data.
 * extendedData: Record<questionnaireType, { blockStats }> for single standard (e.g. from extendedQuestionnairesData).
 * extendedDataByStandard: Record<standardCode, extendedData> for ALL - aggregate across portfolio.
 */
export function getWeakAreasFromExtended(
  standardCode: 'ALL' | string,
  extendedData: Record<string, ExtendedBlockData | null> | null,
  extendedDataByStandard?: Record<string, Record<string, ExtendedBlockData | null>>
): WeakArea[] {
  const candidates: WeakArea[] = [];

  const collectFromBlocks = (blocks: Record<string, ExtendedBlockData | null>, std?: string) => {
    if (!blocks) return;
    const sa = blocks.SELF_ASSESSMENT ?? blocks.selfAssessment;
    if (sa?.blockStats) {
      for (const [blockId, stats] of Object.entries(sa.blockStats)) {
        candidates.push({
          code: blockId,
          name: blockId.replace(/_/g, ' '),
          percent: blockScoreFromStats(stats),
          ...(std && { standard: std }),
        });
      }
    }
    const annex = blocks.ANNEX_A_CONTROLS ?? blocks.annexA;
    if (annex?.blockStats) {
      for (const [blockId, stats] of Object.entries(annex.blockStats)) {
        candidates.push({
          code: blockId,
          name: blockId.replace(/_/g, ' '),
          percent: blockScoreFromStats(stats),
          ...(std && { standard: std }),
        });
      }
    }
    const ctrl = blocks.GDPR_CONTROLS ?? blocks.DORA_CONTROLS ?? blocks.ENS_CONTROLS ??
      blocks.SOC2_CONTROLS ?? blocks.NIS2_CONTROLS ?? blocks.PCI_DSS_2025_CONTROLS ?? blocks.SWIFT_CSCF_2025_CONTROLS;
    if (ctrl?.blockStats) {
      for (const [blockId, stats] of Object.entries(ctrl.blockStats)) {
        candidates.push({
          code: blockId,
          name: blockId.replace(/_/g, ' '),
          percent: blockScoreFromStats(stats),
          ...(std && { standard: std }),
        });
      }
    }
  };

  if (standardCode === 'ALL' && extendedDataByStandard) {
    for (const [std, blocks] of Object.entries(extendedDataByStandard)) {
      collectFromBlocks(blocks, std);
    }
  } else if (extendedData) {
    collectFromBlocks(extendedData);
  }

  return candidates
    .sort((a, b) => a.percent - b.percent)
    .slice(0, 5);
}
